// frontend/src/components/players/PDFViewer.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ChevronRight, ChevronLeft, FileText, Loader, RefreshCw } from 'lucide-react';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';
import { useSecureMedia } from '../../services/mediaService';

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const PDFViewer = ({ mediaId, title }) => {
    const canvasRef = useRef(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);
    const [internalError, setInternalError] = useState(null); // خطاهای داخلی (رندر، بارگذاری سند)

    // هوک امن برای دریافت presigned URL
    const { mediaUrl, isLoading: urlLoading, error: urlError, refetch: refetchMediaUrl } = useSecureMedia(mediaId);

    // رندر صفحه
    const renderPage = useCallback(async (pdf, pageNum) => {
        if (!pdf || !canvasRef.current) return;
        setPageLoading(true);
        try {
            const page = await pdf.getPage(pageNum);
            const scale = window.innerWidth < 768 ? 1.0 : 1.5;
            const viewport = page.getViewport({ scale });
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            canvas.style.width = "100%";
            canvas.style.maxWidth = `${viewport.width}px`;
            canvas.style.height = "auto";
            await page.render({ canvasContext: context, viewport }).promise;
            setInternalError(null);
        } catch (err) {
            console.error("Error rendering page:", err);
            setInternalError("خطا در رندر کردن صفحه. ممکن است فایل PDF خراب باشد.");
        } finally {
            setPageLoading(false);
        }
    }, []);

    // بارگذاری سند PDF با استفاده از mediaUrl
    useEffect(() => {
        let isMounted = true;
        const loadPDF = async () => {
            if (!mediaUrl) return;
            setPdfLoading(true);
            setInternalError(null);
            setPdfDoc(null);
            try {
                const pdf = await pdfjs.getDocument(mediaUrl).promise;
                if (!isMounted) return;
                setNumPages(pdf.numPages);
                setPageNumber(1);
                setPdfDoc(pdf);
            } catch (err) {
                console.error("PDF Load Error:", err);
                if (isMounted) {
                    let msg = "خطا در بارگذاری سند PDF.";
                    if (err.message?.includes("worker")) {
                        msg = "خطا در بارگذاری PDF Worker. لطفاً با پشتیبانی تماس بگیرید.";
                    } else if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
                        msg = "مشکل در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید.";
                    } else if (err.message?.includes("expired")) {
                        msg = "لینک فایل منقضی شده است. در حال تلاش مجدد...";
                        // می‌توانیم به صورت خودکار refetch کنیم؟ ترجیحاً دستی
                    }
                    setInternalError(msg);
                }
            } finally {
                if (isMounted) setPdfLoading(false);
            }
        };
        loadPDF();
        return () => { isMounted = false; };
    }, [mediaUrl]);

    // رندر صفحه هنگام تغییر صفحه یا آماده شدن سند
    useEffect(() => {
        if (pdfDoc) {
            renderPage(pdfDoc, pageNumber);
        }
    }, [pageNumber, pdfDoc, renderPage]);

    // توابع تغییر صفحه
    const goToPrevPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
    const goToNextPage = () => pageNumber < numPages && setPageNumber(pageNumber + 1);

    // تلاش مجدد برای دریافت لینک امن (refetch) و ریست کردن وضعیت داخلی
    const handleRetry = () => {
        setInternalError(null);
        setPdfDoc(null);
        setNumPages(0);
        setPageNumber(1);
        setPdfLoading(true);
        if (urlError) {
            refetchMediaUrl();  // دوباره لینک را می‌گیرد
        } else {
            // اگر خطای داخلی داشتیم، فقط بارگذاری سند را با mediaUrl فعلی دوباره امتحان کن
            setPdfLoading(true);
        }
    };

    // نمایش خطاهای مربوط به دریافت URL
    if (urlError) {
        return (
            <div className="p-8 text-center text-red-400 bg-gray-900 h-[80vh] flex flex-col items-center justify-center rtl">
                <FileText size={48} className="mb-4" />
                <p className="font-bold mb-4">{urlError}</p>
                <button 
                    onClick={handleRetry}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                    <RefreshCw size={18} /> تلاش مجدد
                </button>
            </div>
        );
    }

    // نمایش خطاهای داخلی PDF
    if (internalError) {
        return (
            <div className="p-8 text-center text-red-400 bg-gray-900 h-[80vh] flex flex-col items-center justify-center rtl">
                <FileText size={48} className="mb-4" />
                <p className="font-bold mb-4">{internalError}</p>
                <button 
                    onClick={handleRetry}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
                >
                    <RefreshCw size={18} /> تلاش مجدد
                </button>
            </div>
        );
    }

    // لودینگ اولیه (دریافت لینک یا بارگذاری سند)
    if (urlLoading || pdfLoading) {
        return (
            <div className="p-8 text-center text-white bg-gray-900 h-[80vh] flex flex-col items-center justify-center rtl">
                <Loader size={48} className="animate-spin mb-4" />
                <p>در حال بارگذاری فایل PDF...</p>
            </div>
        );
    }

    // رندر اصلی (موفقیت)
    return (
        <div className="pdf-viewer flex flex-col items-center bg-gray-200 min-h-screen">
            <div className="pdf-document-container overflow-y-auto w-full max-h-[85vh] flex justify-center p-4">
                <div style={{ position: 'relative', width: canvasRef.current?.width || 'auto', height: canvasRef.current?.height || 'auto' }}>
                    {pageLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 z-10">
                            <Loader className="animate-spin mb-2" />
                            <p>در حال بارگذاری صفحه {convertToPersianNumbers(pageNumber)}...</p>
                        </div>
                    )}
                    <canvas ref={canvasRef} className="shadow-xl my-4" style={{ display: pageLoading ? 'none' : 'block' }} />
                </div>
            </div>
            
            {numPages > 1 && (
                <div className="flex justify-center items-center py-3 bg-gray-800 w-full text-white shadow-lg fixed bottom-0 z-10">
                    <button 
                        onClick={goToPrevPage} 
                        disabled={pageNumber <= 1 || pageLoading} 
                        className={`p-2 rounded-full mx-2 transition-colors ${pageNumber <= 1 || pageLoading ? 'text-gray-500 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                        title="صفحه قبلی"
                    >
                        <ChevronRight size={20} />
                    </button>
                    <p className="text-[11px] font-medium mx-4">
                        {pageLoading ? <Loader size={16} className="animate-spin" /> : `صفحه ${convertToPersianNumbers(pageNumber)} از ${convertToPersianNumbers(numPages)}`}
                    </p>
                    <button 
                        onClick={goToNextPage} 
                        disabled={pageNumber >= numPages || pageLoading} 
                        className={`p-2 rounded-full mx-2 transition-colors ${pageNumber >= numPages || pageLoading ? 'text-gray-500 cursor-not-allowed' : 'hover:bg-gray-700'}`}
                        title="صفحه بعدی"
                    >
                        <ChevronLeft size={20} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default PDFViewer;